import { CHECKBOX_DELIMITER, type CampaignField } from "./types";
import { DatePicker } from "../../components/ui/date-picker";

const CUSTOM_INPUT_TYPE: Record<string, string> = { call: "tel", whatsapp: "tel", sms: "tel", email: "email", number: "number", text: "text" };

/** Renders the right control for a campaign question's answer type — shared
 * by the public lead-capture form, the "New customer" modal, and the lead
 * detail panel's editable fields, so multiple_choice/checkboxes/dropdown/
 * date/time behave identically (and the checkboxes join convention with
 * CHECKBOX_DELIMITER, matching the backend, lives in exactly one place). */
export function CustomFieldInput({ field, value, onChange, inputClassName, name }: {
  field: CampaignField;
  value: string;
  onChange: (value: string) => void;
  inputClassName: string;
  /** Distinguishes radio groups when the same field id is rendered more than once on a page. */
  name?: string;
}) {
  if (field.kind === "multiple_choice") {
    return <div className="space-y-1.5">{(field.options || []).map(option => (
      <label key={option} className="flex items-center gap-2 text-sm font-normal text-ink">
        <input type="radio" name={name ?? field.id} required={field.required} checked={value === option} onChange={() => onChange(option)} />
        {option}
      </label>
    ))}</div>;
  }
  if (field.kind === "checkboxes") {
    const selected = value ? value.split(CHECKBOX_DELIMITER) : [];
    return <div className="space-y-1.5">{(field.options || []).map(option => (
      <label key={option} className="flex items-center gap-2 text-sm font-normal text-ink">
        <input
          type="checkbox"
          checked={selected.includes(option)}
          onChange={e => onChange((e.target.checked ? [...selected, option] : selected.filter(o => o !== option)).join(CHECKBOX_DELIMITER))}
        />
        {option}
      </label>
    ))}</div>;
  }
  if (field.kind === "dropdown") {
    return <select required={field.required} value={value} onChange={e => onChange(e.target.value)} className={inputClassName}>
      <option value="" disabled>Choose…</option>
      {(field.options || []).map(option => <option key={option} value={option}>{option}</option>)}
    </select>;
  }
  if (field.kind === "date") return <DatePicker value={value} onChange={onChange} placeholder="Pick date" className={inputClassName} />;
  if (field.kind === "time") return <input required={field.required} type="time" value={value} onChange={e => onChange(e.target.value)} className={inputClassName} />;
  return (
    <input
      required={field.required}
      maxLength={field.kind === "number" ? undefined : 2000}
      type={CUSTOM_INPUT_TYPE[field.kind] ?? "text"}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={inputClassName}
    />
  );
}
